import { type RefObject, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@cx-modules/i18n-provider';
import { CloseIcon } from '@cx-modules/ui';
import { cookieUtils } from '@cx-modules/utils';

import { useOddsGridContext } from '../../context/OddsGridContext';
import { useCloseClickOutside } from '../../hooks/useCloseClickOutside';

import { Pointer } from './Pointer';
import { CloseIconWrapper, NewUserTooltipWrapper, TextWrapper } from './style';

interface NewUserTooltipProps {
  buttonRef: RefObject<HTMLDivElement | null>;
  orientation?: 'left' | 'right';
}

export const NewUserTooltip = ({ buttonRef, orientation = 'right' }: NewUserTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<{ left?: number; right?: number; top: number } | null>(null);

  const closeToolTip = () => {
    cookieUtils.setCookie('newUserGridTooltip', 'true', 60 * 60 * 24 * 365);
    setIsVisible(false);
  };

  const tooltipRef = useRef<HTMLDivElement>(null);
  useCloseClickOutside(tooltipRef, closeToolTip);

  const { t } = useTranslation('label');

  const { isTabletOrDesktop } = useOddsGridContext();
  const isMobile = !isTabletOrDesktop;

  useEffect(() => {
    const seenTooltipCookiePresent = cookieUtils.getCookie('newUserGridTooltip');

    // only show the tooltip on the client side, if the cookie is not present.
    // this avoids a flash of unwanted tooltip on server render for users who have already seen it.
    if (!seenTooltipCookiePresent) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!buttonRef || !buttonRef.current || !isVisible) return;

    const buttonElement = buttonRef.current;
    const parentElement = buttonElement.parentElement;

    if (!parentElement) return;

    const betNameRect = buttonElement.getBoundingClientRect();
    if (orientation === 'left') {
      setPosition({
        left: betNameRect.width + 56,
        top: betNameRect.height / 2 - 1,
      });
    } else {
      setPosition({
        right: 72,
        top: 34,
      });
    }
  }, [buttonRef, isVisible, orientation]);

  if (!isVisible) {
    return null;
  }

  return (
    <NewUserTooltipWrapper
      data-testid="tooltip-for-new-user"
      onClick={closeToolTip}
      orientation={orientation}
      ref={tooltipRef}
      style={
        position
          ? {
              left: position.left !== undefined ? `${position.left}px` : undefined,
              right: position.right !== undefined ? `${position.right}px` : undefined,
              top: `${position.top}px`,
            }
          : {}
      }
    >
      <Pointer orientation={orientation} />
      <CloseIconWrapper isMobile={isMobile} orientation={orientation}>
        <CloseIcon />
      </CloseIconWrapper>
      <TextWrapper orientation={orientation}>{t('CX_ODDS_GRID_TOOLTIP', { ns: 'label' })}</TextWrapper>
    </NewUserTooltipWrapper>
  );
};
